import { useEffect, useRef, useState } from "react";
import { API } from "@/lib/apiClient";
import { PageHeader } from "@/components/common";
import { renderMarkdown } from "@/lib/markdown";
import { Button } from "@/components/ui/button";
import { Sparkles, Send, Loader2, User } from "lucide-react";

const SUGGESTIONS = [
  "Why did my profit fall this month?",
  "Which products make me the most money?",
  "Where am I wasting money?",
  "Which customers owe me money?",
  "How much cash will I need next month?",
  "Give me 5 things I should do this week.",
];

const SESSION_KEY = "khevora_ai_session";

export default function AIAdvisor() {
  const [sessionId] = useState(() => {
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) { s = "sess_" + Math.random().toString(36).slice(2); sessionStorage.setItem(SESSION_KEY, s); }
    return s;
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/ai/history/${sessionId}`, { headers: authHeader() })
      .then((r) => r.json())
      .then((h) => setMessages(h.map((m) => ({ role: m.role, content: m.content }))))
      .catch(() => {});
  }, [sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const authHeader = () => {
    const t = localStorage.getItem("khevora_token");
    return { "Content-Type": "application/json", Authorization: `Bearer ${t}` };
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: authHeader(),
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I couldn't reach the AI service. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const empty = messages.length === 0;

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]">
      <PageHeader title="AI Business Advisor" subtitle="Ask anything about your business — I analyze your real data." />

      <div className="flex-1 min-h-0 rounded-xl border border-border bg-card flex flex-col overflow-hidden animate-fade-up">
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 md:p-6 space-y-5" data-testid="ai-messages">
          {empty && (
            <div className="h-full flex flex-col items-center justify-center text-center max-w-lg mx-auto">
              <div className="grid place-items-center h-14 w-14 rounded-2xl bg-primary text-primary-foreground mb-4">
                <Sparkles className="h-7 w-7" />
              </div>
              <h3 className="font-heading text-xl font-semibold">How can I help you grow today?</h3>
              <p className="text-sm text-muted-foreground mt-1.5">
                I read your sales, expenses, inventory and customers — then tell you what's happening, why, and what to do.
              </p>
              <div className="grid sm:grid-cols-2 gap-2.5 mt-6 w-full">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    data-testid="ai-suggestion"
                    onClick={() => send(s)}
                    className="text-left text-sm rounded-lg border border-border p-3 hover:border-primary/50 hover:bg-primary/5 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
              {m.role === "assistant" && (
                <div className="grid place-items-center h-8 w-8 rounded-lg bg-primary text-primary-foreground shrink-0">
                  <Sparkles className="h-4 w-4" />
                </div>
              )}
              <div className={`max-w-[85%] md:max-w-[75%] rounded-xl px-4 py-3 text-sm leading-relaxed ${
                m.role === "user"
                  ? "bg-secondary text-secondary-foreground"
                  : "bg-primary/5 border border-primary/15"
              }`}>
                {m.role === "assistant"
                  ? (m.content ? <div className="space-y-0.5">{renderMarkdown(m.content)}</div>
                     : <span className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Analyzing your data…</span>)
                  : m.content}
              </div>
              {m.role === "user" && (
                <div className="grid place-items-center h-8 w-8 rounded-lg bg-secondary shrink-0">
                  <User className="h-4 w-4" />
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="border-t border-border p-3 md:p-4">
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
            <input
              data-testid="ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about profit, cash flow, customers, inventory…"
              className="flex-1 rounded-lg border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
            <Button type="submit" disabled={streaming || !input.trim()} data-testid="ai-send" className="px-4">
              {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
